import { Stack } from '@mui/material';
import { FilterValue } from '@schooly/api';
import { Genders } from '@schooly/constants';
import { FC, PropsWithChildren, useCallback } from 'react';
import { $enum } from 'ts-enum-util';

import { DropdownSelect, DropdownSelectProps } from '../DropdownSelect';
import { renderGenderTags } from './GenderExpandedSelect';

type GenderSelectMultipleProps = PropsWithChildren<{
  selectedGenders: Genders[];
  onSelectGender: (v: Genders) => void;
  availableValues?: FilterValue[];
  required?: boolean;
}> &
  Omit<DropdownSelectProps, 'children' | 'renderContent'>;

const OPTIONS = $enum(Genders).map((v) => v);

export const GenderSelectMultiple: FC<GenderSelectMultipleProps> = ({
  selectedGenders,
  onSelectGender,
  placeholder,
  availableValues,
  required,
  ...dropdownProps
}) => {
  const isDisabled = useCallback(
    (gender: Genders) =>
      !!required && selectedGenders.length === 1 && selectedGenders.includes(gender),
    [required, selectedGenders],
  );

  const renderContent = useCallback(() => {
    const genders = availableValues ? OPTIONS.filter((v) => availableValues.includes(v)) : OPTIONS;

    return (
      <Stack direction="row" flexWrap="wrap" gap={1} m={1}>
        {renderGenderTags({
          genders,
          tagProps: (status) => ({
            disabled: isDisabled(status),
            variant: selectedGenders.includes(status) ? 'filled' : undefined,
          }),
          onClick: onSelectGender,
        })}
      </Stack>
    );
  }, [availableValues, onSelectGender, selectedGenders, isDisabled]);

  return (
    <DropdownSelect
      {...dropdownProps}
      placeholder={placeholder}
      hasValues={!!selectedGenders.length}
      renderContent={renderContent}
    >
      {(opened) => (
        <>
          {renderGenderTags({
            genders: selectedGenders,
            tagProps: (gender) => {
              const disabled = isDisabled(gender);
              return {
                size: placeholder ? 'small' : undefined,
                disabled,
                ...(disabled ? { onDelete: undefined } : {}),
              };
            },
            onDelete: opened ? onSelectGender : undefined,
          })}
        </>
      )}
    </DropdownSelect>
  );
};
